// A machine follows its branch to the server that serves it: its edge, or the
// parent after a take-back. Server half: internal/api/failover.go.
import { isStaffLanguage, t } from './staffText'

/** What a server says when asked who serves the machine's branch; null: no answer. */
export interface Serves {
  here: boolean
  parent?: string
  key?: string
  /** From the parent: where the edge holding the branch is reached. */
  edges?: string[]
}

/** What a machine keeps, per origin, of its two addresses. */
export interface Memory {
  key?: string
  /** The parent's address, learnt from the edge. */
  parent?: string
  /** The edge's address, kept at the parent. */
  edge?: string
  /** Set at the edge when the machine left it for the parent. */
  moved?: boolean
}

/** Failed asks of its own edge before the machine asks the parent. */
export const FAILS_BEFORE_ASKING = 3
export const ASK_EVERY_MS = 10_000

/** On the edge: after one ask of it, how many failures in a row, and whether to ask the parent now. */
export function afterAskingEdge(local: Serves | null, fails: number): { fails: number; askParent: boolean } {
  if (local?.here) return { fails: 0, askParent: false }
  return { fails: fails + 1, askParent: fails + 1 >= FAILS_BEFORE_ASKING }
}

/** On the edge, the parent's answer: move only when it says it serves the branch. */
export function shouldMoveToParent(parent: Serves | null): boolean {
  return parent?.here === true
}

/** On the parent: the edge to go back to once an edge holds the floor again — only one the parent names. */
export function backTarget(parent: Serves | null, preferred: string | undefined): string | undefined {
  if (!parent || parent.here) return undefined
  const named = (parent.edges ?? []).map((e) => originOf(e)).filter((e): e is string => !!e)
  return preferred && named.includes(preferred) ? preferred : named[0]
}

/** At the edge after a move: blocked until the parent is heard, which says where to be. */
export function stickyVerdict(parent: Serves | null): 'parent' | 'edge' | 'wait' {
  if (parent === null) return 'wait'
  return parent.here ? 'parent' : 'edge'
}

/** The same machine's address at another server, carrying what that server's page needs. */
export function addressAt(origin: string, pathname: string, search: string, add: Record<string, string>): string {
  const params = new URLSearchParams(search)
  for (const k of ['k', 'e', 'via']) params.delete(k)
  for (const [k, v] of Object.entries(add)) if (v) params.set(k, v)
  const q = params.toString()
  return origin.replace(/\/+$/, '') + pathname + (q ? `?${q}` : '')
}

/** An origin or nothing: only http(s) addresses are followed. */
export function originOf(address: string | null | undefined): string | undefined {
  if (!address) return undefined
  try {
    const u = new URL(address)
    return u.protocol === 'https:' || u.protocol === 'http:' ? u.origin : undefined
  } catch {
    return undefined
  }
}

function memoryKey(branch: string, unit: string): string {
  return `qms.machine.${branch}/${unit}`
}

function load(branch: string, unit: string): Memory {
  try {
    return JSON.parse(window.localStorage.getItem(memoryKey(branch, unit)) ?? '{}') as Memory
  } catch {
    return {}
  }
}

function save(branch: string, unit: string, m: Memory): void {
  try {
    window.localStorage.setItem(memoryKey(branch, unit), JSON.stringify(m))
  } catch {
    // Without storage the machine still runs; it only cannot fail over.
  }
}

async function ask(origin: string, branch: string, unit: string, key?: string, wantKey = false): Promise<Serves | null> {
  const ctl = new AbortController()
  const timer = window.setTimeout(() => ctl.abort(), 5000)
  try {
    const path = `/api/v1/screen/${encodeURIComponent(branch)}/${encodeURIComponent(unit)}/serves${wantKey ? '?key=1' : ''}`
    const same = origin === window.location.origin
    // no-cache, not no-store: no-store makes Chrome preflight every ask; the answer itself is no-store.
    const res = await fetch(same ? path : origin + path, {
      cache: 'no-cache',
      credentials: same ? 'same-origin' : 'omit',
      headers: !same && key ? { 'X-Screen-Key': key } : undefined,
      signal: ctl.signal,
    })
    if (!res.ok) return null
    const body = (await res.json()) as Serves
    return typeof body.here === 'boolean' ? body : null
  } catch {
    return null
  } finally {
    window.clearTimeout(timer)
  }
}

function waiting(on: boolean): void {
  const id = 'qms-failover-wait'
  const shown = document.getElementById(id)
  if (!on) {
    shown?.remove()
    return
  }
  if (shown) return
  const lang = (navigator.language || 'en').slice(0, 2)
  const el = document.createElement('div')
  el.id = id
  el.textContent = t('common.connecting', isStaffLanguage(lang) ? lang : 'en') + '…'
  el.setAttribute('role', 'status')
  el.style.cssText =
    'position:fixed;inset:0;z-index:2147483647;display:flex;align-items:center;justify-content:center;' +
    'background:#000;color:#fff;font:600 32px system-ui,sans-serif'
  document.body.appendChild(el)
}

/**
 * Before the page enrols: keeps the key, and the edge's address the parent was
 * sent from. Call before the ?k= is taken out of the address bar.
 */
export function rememberArrival(branch: string, unit: string): void {
  const params = new URLSearchParams(window.location.search)
  const m = load(branch, unit)
  const key = params.get('k')
  if (key) m.key = key
  const edge = originOf(params.get('e'))
  if (edge && edge !== window.location.origin) m.edge = edge
  if (params.get('via') === 'parent') m.moved = false
  save(branch, unit, m)
  params.delete('e')
  params.delete('via')
  if (params.toString() !== new URLSearchParams(window.location.search).toString()) {
    const q = params.toString()
    window.history.replaceState(null, '', window.location.pathname + (q ? `?${q}` : '') + window.location.hash)
  }
}

/** Watches an addressed machine's server and moves it when its branch is served elsewhere. */
export function startFailover(branch: string, unit: string): void {
  const here = window.location.origin
  const go = (url: string) => window.location.replace(url)

  const onParent = async () => {
    const m = load(branch, unit)
    if (!m.edge) return
    const target = backTarget(await ask(here, branch, unit), m.edge)
    if (target) {
      go(addressAt(target, window.location.pathname, window.location.search, { via: 'parent', k: m.key ?? '' }))
      return
    }
    window.setTimeout(onParent, ASK_EVERY_MS)
  }

  let fails = 0
  const onEdge = async () => {
    const m = load(branch, unit)
    if (m.moved && m.parent) {
      const verdict = stickyVerdict(await ask(m.parent, branch, unit, m.key))
      if (verdict === 'parent') {
        go(addressAt(m.parent, window.location.pathname, window.location.search, { k: m.key ?? '', e: here }))
        return
      }
      if (verdict === 'edge') save(branch, unit, { ...m, moved: false })
      waiting(verdict === 'wait')
      window.setTimeout(onEdge, ASK_EVERY_MS)
      return
    }
    waiting(false)
    const local = await ask(here, branch, unit, undefined, !m.key)
    if (local?.parent || local?.key) {
      const parent = originOf(local.parent) ?? m.parent
      save(branch, unit, { ...m, parent, key: m.key ?? local.key })
    }
    const step = afterAskingEdge(local, fails)
    fails = step.fails
    const parent = load(branch, unit).parent
    if (step.askParent && parent && parent !== here && shouldMoveToParent(await ask(parent, branch, unit, m.key))) {
      save(branch, unit, { ...load(branch, unit), moved: true })
      go(addressAt(parent, window.location.pathname, window.location.search, { k: m.key ?? '', e: here }))
      return
    }
    window.setTimeout(onEdge, ASK_EVERY_MS)
  }

  void (async () => {
    const m = load(branch, unit)
    if (m.edge) return onParent()
    if (m.parent) return onEdge()
    // First start: an edge says where its parent is; a server with no edge says nothing, and is left alone.
    const local = await ask(here, branch, unit, undefined, !m.key)
    if (!local?.parent) return
    save(branch, unit, { ...m, parent: originOf(local.parent), key: m.key ?? local.key })
    window.setTimeout(onEdge, ASK_EVERY_MS)
  })()
}

const STAFF_PARENT = 'qms.edgeParent'
const STAFF_BRANCH = 'qms.edgeBranch'
const STAFF_FAILS = 4
const STAFF_EVERY_MS = 15_000

/** A staff page on an edge: once the edge has not served for a minute and the parent says it serves the branch, sign in there. */
export function startStaffFailover(): void {
  let fails = 0
  const check = async () => {
    let serving = false
    try {
      const res = await fetch('/api/v1/edge/status', { cache: 'no-store' })
      const st = (await res.json()) as { serving?: boolean; parent?: string; branchId?: string }
      serving = st.serving === true
      const parent = originOf(st.parent)
      if (parent) window.localStorage.setItem(STAFF_PARENT, parent)
      if (st.branchId) window.localStorage.setItem(STAFF_BRANCH, st.branchId)
    } catch {
      // Unreachable, or not an edge.
    }
    let parent: string | undefined
    let branch: string | null = null
    try {
      parent = originOf(window.localStorage.getItem(STAFF_PARENT))
      branch = window.localStorage.getItem(STAFF_BRANCH)
    } catch {
      parent = undefined
    }
    if (!parent || !branch || parent === window.location.origin) return
    fails = serving ? 0 : fails + 1
    if (fails >= STAFF_FAILS) {
      try {
        const res = await fetch(`${parent}/api/v1/edge/branch/${encodeURIComponent(branch)}/serves`, { cache: 'no-store', credentials: 'omit' })
        if (shouldMoveToParent(res.ok ? ((await res.json()) as Serves) : null)) {
          window.location.replace(parent + '/')
          return
        }
      } catch {
        // The parent cannot be reached either: stay.
      }
    }
    window.setTimeout(check, STAFF_EVERY_MS)
  }
  void check()
}
